
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { INBOX_ID, ARCHIVE_ID } from "@/utils/contentFormUtils";

/**
 * Custom hook to fetch content data and its associated collection
 */
export const useContentData = (contentId: string) => {
  const [contentData, setContentData] = useState<any>(null);
  const [collectionId, setCollectionId] = useState<string>(INBOX_ID);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const fetchContentData = async () => {
      if (!contentId || !user) return;
      
      setIsLoading(true);
      try {
        // Fetch content item data
        const { data, error } = await supabase
          .from('content_items')
          .select('*')
          .eq('id', contentId)
          .maybeSingle();
        
        if (error) {
          console.error("Error fetching content item:", error);
          throw error;
        }
        
        if (!data) {
          throw new Error(`Content item with ID ${contentId} not found`);
        }
        
        setContentData(data);
        
        // If the content is archived, use ARCHIVE_ID for the collection
        if (data.archived) {
          console.log("Content is archived, setting collection to ARCHIVE_ID");
          setCollectionId(ARCHIVE_ID);
          return;
        }
        
        // Otherwise, fetch associated collection
        const { data: collectionData, error: collectionError } = await supabase
          .from('content_collections')
          .select('collection_id')
          .eq('content_id', contentId)
          .maybeSingle();
        
        if (collectionError) {
          console.error("Error fetching content collection:", collectionError);
          return;
        }
        
        // If content is in a collection, use that collection ID, otherwise use INBOX_ID
        if (collectionData?.collection_id) {
          console.log("Setting collection ID to:", collectionData.collection_id);
          setCollectionId(collectionData.collection_id);
        } else {
          console.log("No collection found, using INBOX_ID:", INBOX_ID);
          setCollectionId(INBOX_ID);
        }
      } catch (error: any) {
        console.error('Error fetching content data:', error);
        toast.error('Could not load content data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchContentData();
  }, [contentId, user]);

  return {
    contentData,
    collectionId,
    isLoading,
    completeFormData: contentData ? {
      id: contentId,
      ...contentData,
      collection_id: collectionId
    } : null
  };
};
